import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/eds';
import { statusView, type StatusTone } from '@/lib/portalStatus';
import { displayCount } from '@/lib/apiError';
import { GiisdecMark } from '@/components/portal/BrandLogo';
import AssistanceLauncher from '@/components/portal/help/AssistanceLauncher';
import {
  TrendingUp, FileText, Briefcase, Building2, ShieldCheck, FileSignature,
  ClipboardList, Database, Bell, LifeBuoy, CreditCard, Sparkles,
  Search, Menu, X, LogOut, ArrowRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  investorService,
  type InvestorProfileSummary,
  type ApplicationRecord,
  type ApplicationTimelineRecord,
  type ApplicationDocumentRecord,
  type ApplicationReviewRecord,
  type NotificationItem,
  type ReviewTimelineEntry,
  type InvestorKycProfile,
  type WorkflowStage,
  type AgreementRecord,
  type PortalDocument,
  InvestorReadError,
} from '@/services/investorService';

/** VUI1-FIX1: which authoritative sources failed to load (UNAVAILABLE ≠ empty / zero / a state). */
type Unavailable = {
  profile: boolean; stages: boolean; kyc: boolean; workflow: boolean;
  agreements: boolean; documents: boolean; notifications: boolean; timeline: boolean;
};
const NONE_UNAVAILABLE: Unavailable = {
  profile: false, stages: false, kyc: false, workflow: false,
  agreements: false, documents: false, notifications: false, timeline: false,
};

import {
  WorkflowProgressTracker,
  RequiredNextAction,
  EnhancedKycForm,
} from '@/components/portal/kyc/KycWorkflow';


import {
  InvestorStatusIndicator,
  InvestorAgreementStatusCards,
  ReviewTimeline,
  NdaRequiredNotice,
  MouRequiredNotice,
  RestrictedDownloadNotice,
} from '@/components/portal/access/AccessGovernance';
import PaymentsCenter from '@/components/portal/payments/PaymentsCenter';
import {
  SupportEnquiryForm,
  NotificationPreferencesPanel,
} from '@/components/portal/support/SupportCenter';

import {
  InvestorOnboardingWalkthrough,
  PaymentReadinessPanel,
  SupportReadinessPanel,
  AgreementsReadinessPanel,
  ApplicationTrackingTimeline,
  PilotDemoScenariosCard,
  InvestorOperationalStatus,
} from '@/components/portal/PilotDemo';
import UatReadinessPanel from '@/components/portal/uat/UatReadinessPanel';
import Phase2ValidationCentre from '@/components/portal/Phase2ValidationCentre';
import InvestorGisEntry from '@/components/portal/investor/InvestorGisEntry';

import {
  AgreementCentre,
  DocumentCentre,
  NotificationCentre,
} from '@/components/portal/investor/InvestorCentres';
import InvestorProfileCentre from '@/components/portal/investor/InvestorProfileCentre';
import UploadField from '@/components/portal/UploadField';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';





type TabKey =
  | 'overview' | 'profile' | 'kyc' | 'documents' | 'permits' | 'tracking'
  | 'restricted' | 'agreements' | 'payments' | 'support' | 'notifications' | 'readiness';

const TABS: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'overview', label: 'Overview', icon: Briefcase },
  { key: 'profile', label: 'Profile Centre', icon: Building2 },
  { key: 'readiness', label: 'Readiness', icon: Sparkles },
  { key: 'kyc', label: 'KYC / Onboarding', icon: ShieldCheck },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'permits', label: 'Regulatory Applications', icon: ClipboardList },
  { key: 'tracking', label: 'Applications', icon: TrendingUp },
  { key: 'restricted', label: 'Restricted Data', icon: Database },
  { key: 'agreements', label: 'NDA / MOU / LOI', icon: FileSignature },
  { key: 'payments', label: 'Payments', icon: CreditCard },
  { key: 'support', label: 'Support', icon: LifeBuoy },
  { key: 'notifications', label: 'Notifications', icon: Bell },
];

const TAB_MAP: Record<TabKey, { label: string; icon: React.ComponentType<{ className?: string }> }> =
  Object.fromEntries(TABS.map((t) => [t.key, { label: t.label, icon: t.icon }])) as Record<
    TabKey,
    { label: string; icon: React.ComponentType<{ className?: string }> }
  >;

// WP2.2-R2: sidebar navigation groups (Command-Centre composition). Every item
// maps to an existing investor tab/panel — no route or logic change.
const NAV_GROUPS: { group: string; items: TabKey[] }[] = [
  { group: 'Workspace', items: ['overview', 'readiness'] },
  { group: 'Identity & Governance', items: ['profile', 'kyc'] },
  { group: 'Workflow', items: ['tracking', 'documents', 'permits', 'agreements', 'restricted'] },
  { group: 'Finance & Support', items: ['payments', 'support', 'notifications'] },
];

const TAB_SUBTITLE: Record<TabKey, string> = {
  overview: 'Your GIISDEC Investor Portal workspace.',
  profile: 'Maintain your personal and organisation information.',
  readiness: 'Readiness: agreements, GIS access, payments and support.',
  kyc: 'Complete KYC verification and track your onboarding workflow.',
  documents: 'Your document centre — request, view and download permitted documents.',
  permits: 'Submit a regulatory application or request. Submitting an application is not permit or licence issuance.',
  tracking: 'Track your applications and their review timeline.',
  restricted: 'Request access to restricted datasets (NDA-gated).',
  agreements: 'Letters of Intent, NDAs and MOUs in one consolidated view.',
  payments: 'Invoices and payment records. Payments are verified manually by GIISDEC Finance.',
  support: 'Contact the GIISDEC support desk and manage notification preferences.',
  notifications: 'Workflow, agreement, payment and system updates.',
};


const InvestorDashboard: React.FC = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const [investor, setInvestor] = useState<InvestorProfileSummary | null>(null);
  const [applications, setApplications] = useState<ApplicationRecord[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [timeline, setTimeline] = useState<ReviewTimelineEntry[]>([]);
  const [kyc, setKyc] = useState<InvestorKycProfile | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowStage[]>([]);
  const [agreements, setAgreements] = useState<AgreementRecord[]>([]);
  const [portalDocs, setPortalDocs] = useState<PortalDocument[]>([]);

  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // P9D: a FAILED applications read is "unavailable", never a measured zero (§29).
  const [appsUnavailable, setAppsUnavailable] = useState(false);
  const [unavailable, setUnavailable] = useState<Unavailable>(NONE_UNAVAILABLE);
  const [reloadKey, setReloadKey] = useState(0);

  // VUI1-FIX1: the mobile/tablet navigation drawer is an intentional modal — focus moves in on
  // open, Tab stays inside, Escape closes, and focus returns to the trigger on close.
  const navTriggerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);
  const drawerWasOpen = useRef(false);
  useEffect(() => {
    if (!sidebarOpen) {
      if (drawerWasOpen.current) { drawerWasOpen.current = false; navTriggerRef.current?.focus(); }
      return;
    }
    drawerWasOpen.current = true;
    drawerCloseRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setSidebarOpen(false); return; }
      if (e.key !== 'Tab' || !drawerRef.current) return;
      const items = Array.from(drawerRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el.getClientRects().length > 0);
      if (items.length === 0) return;
      const first = items[0]; const last = items[items.length - 1]; const active = document.activeElement;
      if (!drawerRef.current.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);


  // All fetches are guarded — geo-api errors surface as inline messaging
  // instead of throwing into the React tree.
  useEffect(() => {
    let cancelled = false;
    setDataLoading(true);
    setLoadError(null);
    (async () => {
      try {
        // VUI1-FIX1: a failed read is recorded as UNAVAILABLE for its source; it is never
        // replaced by a default business state (public visitor, NDA pending, 0 documents …).
        // 404/403 on the profile is an authoritative "no investor profile", not an outage.
        let profileFailed = false;
        let profileNone = false;
        const [profileRes, appsRes, notifsRes, tlRes] = await Promise.all([
          investorService.getProfile().catch((e: unknown) => {
            const st = e instanceof InvestorReadError ? e.status : 0;
            if (st !== 404 && st !== 403) profileFailed = true; else profileNone = true;
            return null;
          }),
          investorService.listApplications().catch(() => ({ data: [], error: 'load_failed', status: 0 })),
          investorService.listNotifications().catch(() => ({ data: [], error: 'load_failed', status: 0 })),
          investorService.listReviewTimeline().catch(() => ({ data: [], error: 'load_failed', status: 0 })),
        ]);
        if (cancelled) return;
        setInvestor(profileRes ?? null);
        setApplications(Array.isArray(appsRes?.data) ? appsRes.data : []);
        // G1C1: with no investor record (authoritative 404/403 on the profile), the investor
        // applications read answers 404/403 too — that is "no applications", not an outage.
        const appsFailed = !!appsRes?.error && !(profileNone && (appsRes?.status === 404 || appsRes?.status === 403));
        setAppsUnavailable(appsFailed);
        setNotifications(Array.isArray(notifsRes?.data) ? notifsRes.data : []);
        setTimeline(Array.isArray(tlRes?.data) ? tlRes.data : []);
        // KYC + workflow governance reads — independently guarded so a failure
        // here never blocks the rest of the workspace from rendering.
        const failed = { kyc: false, workflow: false, agreements: false, documents: false };
        const [kycRes, wfRes, agRes, docsRes] = await Promise.all([
          investorService.getKycProfile().catch(() => { failed.kyc = true; return null; }),
          investorService.getWorkflowStages().catch(() => { failed.workflow = true; return [] as WorkflowStage[]; }),
          investorService.listAgreements().catch(() => { failed.agreements = true; return [] as AgreementRecord[]; }),
          investorService.listDocuments().catch(() => { failed.documents = true; return [] as PortalDocument[]; }),
        ]);
        if (cancelled) return;
        setKyc(kycRes ?? null);
        setWorkflow(Array.isArray(wfRes) ? wfRes : []);
        setAgreements(Array.isArray(agRes) ? agRes : []);
        setPortalDocs(Array.isArray(docsRes) ? docsRes : []);

        const next: Unavailable = {
          profile: profileFailed,
          stages: profileFailed || !!profileRes?.stagesUnavailable,
          kyc: failed.kyc || profileFailed || !!profileRes?.kycUnavailable,
          workflow: failed.workflow,
          agreements: failed.agreements,
          documents: failed.documents,
          notifications: !!notifsRes?.error,
          timeline: !!tlRes?.error || appsFailed,
        };
        setUnavailable(next);
        // One workspace-level notice; affected values are individually marked unavailable.
        const anyErr = Object.values(next).some(Boolean);
        if (anyErr) setLoadError('Some workspace information could not be loaded right now. Items marked “unavailable” have not changed — this is a connection problem, not a change to your account.');
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load workspace data.');
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const status = investor?.status ?? 'public_visitor';
  const ndaSigned = investor?.ndaSigned ?? false;
  const mouSigned = investor?.mouSigned ?? false;
  const isVerified = ['verified_investor', 'shortlisted_investor', 'approved_investor', 'strategic_partner', 'verified'].includes(status);

  // ── Identity + live derived values (no fabricated data) ──
  const displayName = profile?.full_name || investor?.fullName || user?.email || 'Investor';
  const displayEmail = user?.email || '';
  const initials = (displayName.match(/\b\w/g) || ['I']).slice(0, 2).join('').toUpperCase();
  const unreadCount = notifications.filter((n) => !n.read).length;
  const readableStatus = unavailable.profile
    ? 'Status unavailable'
    : status.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const retry = () => setReloadKey((k) => k + 1);
  // Workflow / next-step panels mix profile, agreement stages, workflow and KYC: any failed
  // source makes their derived state unknown, so they are replaced, not defaulted.
  const workflowUnknown = unavailable.profile || unavailable.stages || unavailable.workflow || unavailable.kyc;

  const go = (t: TabKey) => { setTab(t); setSidebarOpen(false); };
  const handleSignOut = async () => { await signOut(); navigate('/'); };

  const filteredNavGroups = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return NAV_GROUPS;
    return NAV_GROUPS
      .map((g) => ({ ...g, items: g.items.filter((k) => TAB_MAP[k].label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [navQuery]);

  // Overview KPI cards — governed loading marker while hydrating; live counts
  // and the live KYC completeness score. No sample/hard-coded values.
  const kpis = [
    { icon: Briefcase, label: 'Active Applications', value: dataLoading ? '…' : (displayCount(!appsUnavailable, applications.length) === null ? 'Unavailable' : String(applications.length)), sub: 'Applications, documents & data requests' },
    { icon: FileText, label: 'Document Centre', value: dataLoading ? '…' : (unavailable.documents ? 'Unavailable' : String(portalDocs.length)), sub: unavailable.documents ? 'Could not be loaded' : 'Investor & KYC documents' },
    { icon: Bell, label: 'Notifications', value: dataLoading ? '…' : (unavailable.notifications ? 'Unavailable' : String(notifications.length)), sub: unavailable.notifications ? 'Could not be loaded' : (unreadCount > 0 ? `${unreadCount} unread` : 'All caught up') },
    {
      icon: ShieldCheck,
      label: 'KYC Completeness',
      value: dataLoading ? '…' : (unavailable.kyc ? 'Unavailable' : (typeof kyc?.completenessScore === 'number' ? `${Math.round(kyc.completenessScore)}%` : '—')),
      sub: unavailable.kyc ? 'Could not be loaded' : (kyc ? 'Verification progress' : 'Awaiting KYC data'),
    },
  ];
  const kycPct = !unavailable.kyc && typeof kyc?.completenessScore === 'number' ? Math.max(0, Math.min(100, Math.round(kyc.completenessScore))) : null;

  const sidebarInner = (
    <>
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-eds-forest-2 flex-shrink-0">
        <GiisdecMark className="w-8 h-8" />
        <div className="leading-tight">
          <div className="font-eds-display font-bold text-sm text-white">GIISDEC</div>
          <div className="text-[9px] uppercase tracking-widest text-white/50">Investor Portal</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {filteredNavGroups.length === 0 && (
          <p className="px-2 text-xs text-white/50">No sections match “{navQuery}”.</p>
        )}
        {filteredNavGroups.map((g) => (
          <div key={g.group}>
            <div className="px-2 text-[10px] font-semibold uppercase tracking-widest text-white/60 mb-1.5">{g.group}</div>
            <div className="space-y-0.5">
              {g.items.map((k) => {
                const Icon = TAB_MAP[k].icon;
                const active = tab === k;
                return (
                  <button
                    key={k}
                    onClick={() => go(k)}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-eds-md text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint/50 ${
                      active ? 'bg-eds-forest-2 text-eds-mint font-semibold' : 'text-white/70 hover:text-white hover:bg-eds-forest-2/60'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{TAB_MAP[k].label}</span>
                    {k === 'notifications' && unreadCount > 0 && (
                      <span className="ml-auto text-[10px] font-bold bg-eds-emerald text-eds-primary-fg rounded-eds-pill px-1.5 py-0.5">{unreadCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-eds-forest-2 flex-shrink-0">
        <div className="rounded-eds-md bg-white/5 border border-white/10 p-3">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 flex-shrink-0 rounded-eds-md bg-eds-emerald text-eds-primary-fg flex items-center justify-center text-xs font-bold">{initials}</span>
            <div className="min-w-0 leading-tight">
              <div className="text-sm font-semibold text-white truncate">{displayName}</div>
              <div className="text-[11px] text-white/50">Role · Investor</div>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 border border-white/15 rounded-eds-md py-1.5 transition"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-eds-bg font-eds-body flex text-eds-fg">
      {/* ── Desktop sidebar (Command-Centre left rail) ── */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-eds-forest text-white sticky top-0 h-screen">
        {sidebarInner}
      </aside>

      {/* ── Mobile drawer ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-eds-forest/70 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <aside
            id="investor-mobile-nav"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Workspace navigation"
            className="absolute inset-y-0 left-0 w-64 flex flex-col bg-eds-forest text-white shadow-eds-md"
          >
            <button
              ref={drawerCloseRef}
              onClick={() => setSidebarOpen(false)}
              className="absolute top-3 right-2 z-10 inline-flex items-center justify-center w-10 h-10 rounded-eds-md text-white/80 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint"
              aria-label="Close navigation"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
            {sidebarInner}
          </aside>
        </div>
      )}

      {/* ── Main column ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="h-16 flex items-center gap-3 px-4 sm:px-6 bg-eds-surface border-b border-eds-border sticky top-0 z-30">
          {/* VUI1-FIX1: visible, 40×40 target (was a 20×20 near-white icon). */}
          <button
            ref={navTriggerRef}
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden -ml-2 inline-flex items-center justify-center w-10 h-10 flex-shrink-0 rounded-eds-md text-eds-fg hover:bg-eds-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            aria-controls="investor-mobile-nav"
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-eds-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              placeholder="Search workspace sections…"
              aria-label="Search workspace sections"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-eds-md border border-eds-border bg-eds-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
            />
          </div>
          <button
            onClick={() => go('notifications')}
            className="relative text-eds-muted hover:text-eds-fg transition"
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold text-eds-primary-fg bg-eds-emerald rounded-eds-pill">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => go('profile')}
            className="flex items-center gap-2 pl-1"
            aria-label="Open profile centre"
          >
            <span className="w-8 h-8 rounded-eds-md bg-eds-forest text-white flex items-center justify-center text-xs font-bold">{initials}</span>
            <span className="hidden md:block text-left leading-tight">
              <span className="block text-sm font-semibold text-eds-fg truncate max-w-[140px]">{displayName}</span>
              <span className="block text-sm font-medium text-eds-muted-fg truncate max-w-[140px]">{displayEmail || 'Investor'}</span>
            </span>
          </button>
        </header>

        {/* Content region */}
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 max-w-7xl w-full mx-auto">
          {/* Page header */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-eds-display text-2xl font-bold text-eds-fg">{TAB_MAP[tab].label}</h1>
              <p className="text-sm text-eds-muted-fg mt-1 max-w-2xl">{TAB_SUBTITLE[tab]}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {unavailable.profile ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-eds-pill border border-eds-border bg-eds-surface text-eds-fg">Account status unavailable</span>
              ) : (
                <InvestorStatusIndicator status={status} />
              )}
              {unavailable.stages ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-eds-pill border border-eds-border bg-eds-surface text-eds-fg">Agreement status unavailable</span>
              ) : (<>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-eds-pill border ${ndaSigned ? 'bg-eds-emerald/10 text-eds-emerald border-eds-emerald/30' : 'bg-eds-bg text-eds-muted-fg border-eds-border'}`}>
                NDA: {ndaSigned ? 'signed' : 'pending'}
              </span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-eds-pill border ${mouSigned ? 'bg-eds-emerald/10 text-eds-emerald border-eds-emerald/30' : 'bg-eds-bg text-eds-muted-fg border-eds-border'}`}>
                MOU: {mouSigned ? 'signed' : 'pending'}
              </span>
              </>)}
            </div>
          </div>

          {loadError && (
            <div
              role="status"
              className="mb-5 px-3 py-2 rounded-eds-md text-xs border border-eds-gold/30 bg-eds-gold/10 text-eds-earth flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <span>{loadError}</span>
              <button
                type="button"
                onClick={retry}
                className="font-semibold underline underline-offset-2 rounded-eds-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
              >
                Try again
              </button>
            </div>
          )}

          {tab === 'overview' && (
            <div className="space-y-6">
              {/* KPI row — live counts + live KYC completeness (no sample values) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {kpis.map((c) => (
                  <div key={c.label} className="bg-eds-surface border border-eds-border rounded-eds-lg p-5 shadow-eds-sm">
                    <div className="flex items-center justify-between">
                      <span className="w-9 h-9 rounded-eds-md bg-eds-forest/5 text-eds-forest flex items-center justify-center">
                        <c.icon className="w-5 h-5" />
                      </span>
                    </div>
                    <div className="mt-3 font-eds-display text-2xl font-bold text-eds-fg tabular-nums">{c.value}</div>
                    <div className="text-sm font-medium text-eds-fg mt-1">{c.label}</div>
                    <div className="text-sm font-medium text-eds-muted-fg mt-0.5">{c.sub}</div>
                  </div>
                ))}
              </div>

              {/* Two-column: main workflow column + right readiness/activity rail */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 space-y-6">
                  {workflowUnknown ? (
                    <UnavailableNotice what="Your next steps and workflow status are" onRetry={retry} />
                  ) : (<>
                  <RequiredNextAction
                    serverStages={workflow}
                    profile={investor}
                    kyc={kyc}
                    onJump={(t) => go(t as TabKey)}
                  />
                  <WorkflowProgressTracker
                    serverStages={workflow}
                    profile={investor}
                    onJump={(t) => go(t as TabKey)}
                  />
                  <InvestorOnboardingWalkthrough
                    profile={investor}
                    applicationCount={applications.length}
                    onJump={(t) => go(t as TabKey)}
                  />
                  </>)}
                  {unavailable.profile || unavailable.stages ? (
                    <UnavailableNotice what="Agreement status (NDA / MOU / LOI) is" />
                  ) : (
                    <InvestorAgreementStatusCards profile={investor} />
                  )}
                  {unavailable.timeline ? (
                    <UnavailableNotice what="Application activity is" />
                  ) : (
                    <ApplicationTrackingTimeline applications={applications} timeline={timeline} />
                  )}
                </div>

                <aside className="space-y-6">
                  {/* P9E: Investor GIS entry — shown only from the server
                      entitlement contract, never from status/role/agreements. */}
                  <InvestorGisEntry key={reloadKey} />

                  {/* KYC readiness ring — live completeness score only */}
                  <div className="bg-eds-surface border border-eds-border rounded-eds-lg p-5 shadow-eds-sm">
                    <h3 className="font-eds-display text-sm font-semibold text-eds-fg mb-4">Onboarding readiness</h3>
                    <div className="flex items-center gap-4">
                      <div
                        className="relative w-20 h-20 rounded-eds-pill flex items-center justify-center flex-shrink-0"
                        style={{
                          background: kycPct === null
                            ? 'conic-gradient(hsl(var(--eds-border)) 0deg 360deg)'
                            : `conic-gradient(hsl(var(--eds-emerald)) ${kycPct * 3.6}deg, hsl(var(--eds-border)) ${kycPct * 3.6}deg 360deg)`,
                        }}
                        role="img"
                        aria-label={kycPct === null ? 'KYC completeness awaiting data' : `KYC completeness ${kycPct}%`}
                      >
                        <div className="w-14 h-14 rounded-eds-pill bg-eds-surface flex items-center justify-center font-eds-display text-lg font-bold text-eds-fg">
                          {kycPct === null ? '—' : `${kycPct}%`}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-eds-fg">KYC completeness</div>
                        <div className="text-sm font-medium text-eds-muted-fg mt-0.5">{readableStatus}</div>
                        <button
                          onClick={() => go('kyc')}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-eds-emerald hover:text-eds-emerald-2 transition"
                        >
                          Go to KYC <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {!unavailable.profile && (
                    <InvestorOperationalStatus
                      profile={investor}
                      applicationCount={applications.length}
                    />
                  )}

                  {/* Recent notifications — live rows, governed empty */}
                  <div className="bg-eds-surface border border-eds-border rounded-eds-lg p-5 shadow-eds-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-eds-display text-sm font-semibold text-eds-fg">Recent activity</h3>
                      <button
                        onClick={() => go('notifications')}
                        className="text-xs font-semibold text-eds-emerald hover:text-eds-emerald-2 transition inline-flex items-center gap-1"
                      >
                        View all <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {dataLoading ? (
                      <p className="text-sm font-medium text-eds-muted-fg">Loading activity…</p>
                    ) : unavailable.notifications ? (
                      <p className="text-sm font-medium text-eds-muted-fg">Recent activity is temporarily unavailable. This does not mean nothing has happened.</p>
                    ) : notifications.length === 0 ? (
                      <p className="text-sm font-medium text-eds-muted-fg">No notifications yet. Workflow, agreement and payment updates will appear here.</p>
                    ) : (
                      <ul className="space-y-3">
                        {notifications.slice(0, 5).map((n) => (
                          <li key={n.id} className="flex items-start gap-2.5">
                            <span className={`mt-1.5 w-1.5 h-1.5 rounded-eds-pill flex-shrink-0 ${n.read ? 'bg-eds-border' : 'bg-eds-emerald'}`} />
                            <div className="min-w-0">
                              <div className="text-sm text-eds-fg truncate">{String(n.title ?? 'Update')}</div>
                              {n.createdAt && (
                                <div className="text-sm text-eds-muted-fg">{new Date(n.createdAt).toLocaleDateString()}</div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </aside>
              </div>
            </div>
          )}

          {tab !== 'overview' && (
            <div className="bg-eds-surface border border-eds-border rounded-eds-lg p-6 shadow-eds-sm">
              {tab === 'profile' && (
                <InvestorProfileCentre />
              )}

            {tab === 'readiness' && (
              <div className="space-y-6">
                {/* Phase 2 — user-facing validation support (not admin governance) */}
                <Phase2ValidationCentre audience="investor" onJump={(t) => setTab(t as TabKey)} />

                {/* HF1-FIX1: no implementation/credential diagnostics in the investor workspace.
                    GIS access is the server entitlement (same component as Overview), never a
                    KYC/agreement inference. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {unavailable.profile || unavailable.stages ? (
                    <UnavailableNotice what="Agreement readiness is" onRetry={retry} />
                  ) : (
                    <AgreementsReadinessPanel profile={investor} onJump={(t) => setTab(t as TabKey)} />
                  )}
                  <InvestorGisEntry key={reloadKey} />
                  <PaymentReadinessPanel />
                  <SupportReadinessPanel onJump={(t) => setTab(t as TabKey)} />
                </div>

                {/* Cross-Application UAT / Pilot Acceptance — authorized admins only.
                    Non-admins never issue the request; the panel itself also
                    degrades to a restricted smart empty state if geo-api rejects. */}
                {profile?.role === 'admin' && <UatReadinessPanel />}
                <PilotDemoScenariosCard onJump={(t) => setTab(t as TabKey)} />
                <ApplicationTrackingTimeline applications={applications} timeline={timeline} />
              </div>
            )}



            {tab === 'kyc' && (
              <div className="space-y-6">
                <EnhancedKycForm profile={kyc} />
                <div>
                  <h3 className="font-semibold text-eds-text mb-3 text-sm">Workflow progress</h3>
                  <WorkflowProgressTracker
                    serverStages={workflow}
                    profile={investor}
                    onJump={(t) => setTab(t as TabKey)}
                  />
                </div>
              </div>
            )}

            {tab === 'documents' && (
              <div className="space-y-6">
                {/* Consolidated Document Centre — grouped, permission-aware */}
                {unavailable.documents ? (
                  <UnavailableNotice what="Your documents are" onRetry={retry} />
                ) : (
                  <DocumentCentre documents={portalDocs} loading={dataLoading} />
                )}
                <div>
                  <h3 className="font-semibold text-eds-text mb-3 text-sm">Request a document</h3>
                  {unavailable.profile || unavailable.stages ? (
                    <UnavailableNotice what="Document request eligibility is" onRetry={retry} />
                  ) : (
                    <DocumentRequestShell ndaSigned={ndaSigned} mouSigned={mouSigned} verified={isVerified} />
                  )}
                </div>
              </div>
            )}

            {tab === 'permits' && <PermitShell />}
            {tab === 'tracking' && (
              <div className="space-y-6">
                <TrackingShell rows={applications} unavailable={appsUnavailable} />
                <div>
                  <h3 className="font-semibold text-eds-text mb-3 text-sm">Approval / Review Timeline</h3>
                  <ReviewTimeline entries={timeline} />
                </div>
              </div>
            )}
            {tab === 'restricted' && (unavailable.profile || unavailable.stages
              ? <UnavailableNotice what="Restricted-data eligibility is" onRetry={retry} />
              : <RestrictedDataShell ndaSigned={ndaSigned} mouSigned={mouSigned} />)}
            {tab === 'agreements' && (
              <div className="space-y-6">
                {unavailable.profile || unavailable.stages || unavailable.agreements || unavailable.workflow ? (
                  <UnavailableNotice what="Your agreements (LOI / NDA / MOU) are" onRetry={retry} />
                ) : (<>
                <InvestorAgreementStatusCards profile={investor} />
                {/* Consolidated Agreement Centre — LOI / NDA / MOU in one view */}
                <AgreementCentre
                  agreements={agreements}
                  loading={dataLoading}
                  workflow={workflow}
                  profile={investor}
                />
                </>)}
                <div>
                  <h3 className="font-semibold text-eds-text mb-3 text-sm">Request a new agreement</h3>
                  <AgreementsShell />
                </div>
              </div>
            )}

            {tab === 'payments' && <PaymentsCenter />}
            {tab === 'support' && (
              <div className="space-y-6">
                <SupportEnquiryForm />
                <div>
                  <h3 className="font-semibold text-eds-text mb-3 text-sm">Notification preferences</h3>
                  <NotificationPreferencesPanel />
                </div>
              </div>
            )}
            {tab === 'notifications' && <NotificationCentre rows={notifications} />}
            </div>
          )}
        </main>
      </div>

      <AssistanceLauncher onJump={(t) => go(t as TabKey)} />
    </div>
  );
};

/* ───────────────── helpers ───────────────── */

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-eds-fg uppercase tracking-wider">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls = 'w-full px-3 py-2 border border-eds-border rounded-eds-md text-sm bg-eds-surface-2 text-eds-text focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-emerald/40';
const submitCls = 'inline-flex items-center gap-2 px-4 py-2 bg-eds-emerald text-white text-sm font-medium rounded-eds-md hover:opacity-90 transition';

/* ───────────────── pilot shells (forms wired to geo-api) ───────────────── */

// Small inline status renderer — success/failure messaging shared across all
// pilot form shells. Never renders raw objects; always coerces to string.
type SubmitState = { ok: true } | { ok: false; error: string } | null;
const SubmitStatus: React.FC<{ state: SubmitState; successLabel?: string }> = ({ state, successLabel }) => {
  if (!state) return null;
  if (state.ok) return <span className="text-sm text-eds-emerald-2">{successLabel ?? 'Submitted.'}</span>;
  // Narrow via property presence so the failure branch resolves under the
  // project's compiler settings; never render a raw object.
  const message = 'error' in state ? state.error : 'Submission failed.';
  return <span className="text-sm text-eds-danger">{message}</span>;
};

/* Legacy basic KycShell removed — replaced by EnhancedKycForm. */

/** VUI1-FIX1: truthful stand-in for a section whose authoritative data could not be loaded. */
const UnavailableNotice: React.FC<{ what: string; onRetry?: () => void }> = ({ what, onRetry }) => (
  <div role="status" className="bg-eds-surface border border-dashed border-eds-border rounded-eds-lg p-5">
    <p className="text-sm font-semibold text-eds-fg">{what} temporarily unavailable</p>
    <p className="text-sm text-eds-muted-fg mt-1">
      This information could not be loaded right now. This does not mean your status has changed.
    </p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-eds-md border border-eds-border text-eds-fg hover:bg-eds-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
      >
        Try again
      </button>
    )}
  </div>
);

const DocumentRequestShell: React.FC<{ ndaSigned: boolean; mouSigned: boolean; verified: boolean }> = ({ ndaSigned, mouSigned, verified }) => {
  const [state, setState] = useState<SubmitState>(null);
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true); setState(null);
    const fd = new FormData(e.currentTarget);
    const res = await investorService.requestDocument({
      documentType: String(fd.get('documentType') || ''),
      projectId: String(fd.get('projectId') || '') || undefined,
      reason: String(fd.get('reason') || ''),
    });
    setBusy(false);
    setState(res?.error ? { ok: false, error: String(res.error) } : { ok: true });
  };
  return (
    <div className="space-y-4">
      {!verified && <RestrictedDownloadNotice reason="VERIFICATION" />}
      <NdaRequiredNotice signed={ndaSigned} />
      <MouRequiredNotice signed={mouSigned} />
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Document type"><input name="documentType" className={inputCls} required /></Field>
        <Field label="Project ID (optional)"><input name="projectId" className={inputCls} /></Field>
        <div className="md:col-span-2">
          <Field label="Reason for request"><textarea name="reason" rows={3} className={inputCls} required /></Field>
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button className={submitCls} type="submit" disabled={!verified || busy}>{busy ? 'Submitting…' : 'Submit request'}</button>
          <SubmitStatus state={state} successLabel="Document request submitted." />
        </div>
      </form>
    </div>
  );
};

const PermitShell: React.FC = () => {
  const [state, setState] = useState<SubmitState>(null);
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true); setState(null);
    const fd = new FormData(e.currentTarget);
    const res = await investorService.applyForPermit({
      permitType: String(fd.get('permitType') || ''),
      projectId: String(fd.get('projectId') || '') || undefined,
      region: String(fd.get('region') || ''),
      notes: String(fd.get('notes') || ''),
    });
    setBusy(false);
    setState(res?.error ? { ok: false, error: String(res.error) } : { ok: true });
  };
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Application type"><input name="permitType" className={inputCls} required /></Field>
      <Field label="Project ID (optional)"><input name="projectId" className={inputCls} /></Field>
      <Field label="Region"><input name="region" className={inputCls} required /></Field>
      <div className="md:col-span-2"><Field label="Notes"><textarea name="notes" rows={3} className={inputCls} /></Field></div>
      <div className="md:col-span-2 flex items-center gap-3">
        <button className={submitCls} type="submit" disabled={busy}>{busy ? 'Submitting…' : 'Submit application'}</button>
        <SubmitStatus state={state} successLabel="Application submitted." />
      </div>
    </form>
  );
};

const formatApplicationStatus = (value?: string) =>
  value
    ? value
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

// P9D: application STATUS is rendered through the central Portal status registry
// (portalStatus.ts), never a local label. `approved` resolves to "Approved" — it
// is an application decision, not permit/licence/certificate issuance. Unknown
// states resolve neutral. Non-status fields (type, stage, event_type) keep the
// plain humaniser above.
const edsTone = (t: StatusTone): 'neutral' | 'info' | 'success' | 'warning' | 'danger' => {
  switch (t) {
    case 'positive': return 'success';
    case 'negative': return 'danger';
    case 'info': return 'info';
    case 'warning': return 'warning';
    default: return 'neutral';
  }
};

const AppStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const view = statusView('application', status);
  return <Badge tone={edsTone(view.tone)}>{view.label}</Badge>;
};

const formatApplicationDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const normalizeApplicationRecord = (
  application: ApplicationRecord
): ApplicationRecord => ({
  ...application,
  reference:
    application.reference ??
    application.application_number ??
    application.id,
  type:
    application.type ??
    application.application_type ??
    'application',
  submittedAt:
    application.submittedAt ??
    application.submitted_at ??
    undefined,
  currentStage:
    application.currentStage ??
    application.current_stage ??
    undefined,
  createdAt:
    application.createdAt ??
    application.created_at ??
    undefined,
  updatedAt:
    application.updatedAt ??
    application.updated_at ??
    undefined,
});

const TrackingShell: React.FC<{ rows: ApplicationRecord[]; unavailable?: boolean }> = ({ rows, unavailable = false }) => {
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationRecord | null>(null);

  const [applicationDetail, setApplicationDetail] =
    useState<ApplicationRecord | null>(null);

  const [applicationTimeline, setApplicationTimeline] = useState<
    ApplicationTimelineRecord[]
  >([]);

  const [applicationDocuments, setApplicationDocuments] = useState<
    ApplicationDocumentRecord[]
  >([]);

  const [applicationReviews, setApplicationReviews] = useState<
    ApplicationReviewRecord[]
  >([]);

  const [applicationUploadFiles, setApplicationUploadFiles] = useState<File[]>([]);
  const [applicationUploadType, setApplicationUploadType] = useState('supporting_document');
  const [applicationUploadTitle, setApplicationUploadTitle] = useState('');
  const [applicationUploadBusy, setApplicationUploadBusy] = useState(false);
  const [applicationUploadError, setApplicationUploadError] = useState<string | null>(null);
  const [applicationUploadSuccess, setApplicationUploadSuccess] = useState<string | null>(null);

  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);

  const openApplication = async (application: ApplicationRecord) => {
    setSelectedApplication(application);
    setApplicationDetail(null);
    setApplicationTimeline([]);
    setApplicationDocuments([]);
    setApplicationReviews([]);
    setWorkspaceError(null);
    setWorkspaceLoading(true);

    try {
      const [
        detailResult,
        timelineResult,
        documentResult,
        reviewResult,
      ] = await Promise.all([
        investorService.getApplication(application.id),
        investorService.getApplicationTimeline(application.id),
        investorService.listApplicationDocuments(application.id),
        investorService.listApplicationReviews(application.id),
      ]);

      if (detailResult.error || !detailResult.data) {
        throw new Error('detail');
      }

      setApplicationDetail(
        normalizeApplicationRecord(detailResult.data)
      );

      setApplicationTimeline(
        Array.isArray(timelineResult.data?.items)
          ? timelineResult.data.items
          : []
      );

      setApplicationDocuments(
        Array.isArray(documentResult.data?.items)
          ? documentResult.data.items
          : []
      );

      setApplicationReviews(
        Array.isArray(reviewResult.data?.items)
          ? reviewResult.data.items
          : []
      );
    } catch {
      setWorkspaceError(
        'Application details could not be loaded. Please try again.'
      );
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const effectiveApplication =
    applicationDetail ??
    (selectedApplication
      ? normalizeApplicationRecord(selectedApplication)
      : null);

  const handleApplicationDocumentUpload = async () => {
    if (!selectedApplication?.id) {
      setApplicationUploadError('No application is selected.');
      return;
    }

    const file = applicationUploadFiles[0];
    if (!file) {
      setApplicationUploadError('Select a document to upload.');
      return;
    }

    const docType = applicationUploadType.trim();
    if (!docType) {
      setApplicationUploadError('Document type is required.');
      return;
    }

    setApplicationUploadBusy(true);
    setApplicationUploadError(null);
    setApplicationUploadSuccess(null);

    try {
      const result = await investorService.uploadApplicationDocument(
        selectedApplication.id,
        file,
        {
          docType,
          title: applicationUploadTitle,
          metadata: {
            source: 'investor_portal',
          },
        }
      );

      if (result.error || !result.data) {
        setApplicationUploadError(
          result.error || 'Document upload could not be completed.'
        );
        return;
      }

      const [documentsResult, timelineResult] = await Promise.all([
        investorService.listApplicationDocuments(selectedApplication.id),
        investorService.getApplicationTimeline(selectedApplication.id),
      ]);

      setApplicationDocuments(documentsResult.data?.items ?? []);
      setApplicationTimeline(timelineResult.data?.items ?? []);

      setApplicationUploadFiles([]);
      setApplicationUploadTitle('');
      setApplicationUploadSuccess('Document uploaded successfully.');
    } catch {
      setApplicationUploadError(
        'Document upload could not be completed. Please try again.'
      );
    } finally {
      setApplicationUploadBusy(false);
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        {rows.length === 0 ? (
          unavailable ? (
            <p className="text-sm font-medium text-eds-muted-fg">
              Your applications could not be loaded right now. This does not mean you
              have none — please refresh to try again.
            </p>
          ) : (
            <p className="text-sm font-medium text-eds-muted-fg">
              No applications yet. Submit a regulatory application, document, or
              restricted-data request to populate this list.
            </p>
          )
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase text-eds-fg">
              <tr>
                <th className="py-2">Ref</th>
                <th>Type</th>
                <th>Status</th>
                <th>Submitted</th>
                <th className="text-right">Details</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-eds-border hover:bg-eds-surface-2/60 transition"
                >
                  <td className="py-2 font-mono">
                    {String(r.reference || r.id)}
                  </td>

                  <td>{formatApplicationStatus(r.type)}</td>

                  <td><AppStatusBadge status={r.status} /></td>

                  <td>{formatApplicationDate(r.submittedAt)}</td>

                  <td className="text-right">
                    <button
                      type="button"
                      onClick={() => void openApplication(r)}
                      className="text-xs font-semibold text-eds-emerald hover:text-eds-emerald-2 transition"
                    >
                      View details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog
        open={Boolean(selectedApplication)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedApplication(null);
            setApplicationDetail(null);
            setApplicationTimeline([]);
            setApplicationDocuments([]);
            setApplicationReviews([]);
            setWorkspaceError(null);
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto border-eds-border bg-eds-surface text-eds-text">
          <DialogHeader>
            <DialogTitle>Application details</DialogTitle>
            <DialogDescription>
              Review the current application record, documents and workflow activity.
            </DialogDescription>
          </DialogHeader>

          {workspaceLoading ? (
            <div className="py-8 text-center text-sm text-eds-muted-fg">
              Loading application workspace...
            </div>
          ) : workspaceError ? (
            <div className="rounded-xl border border-eds-border bg-eds-surface-2 p-4">
              <p className="text-sm font-semibold text-eds-text">
                Application workspace unavailable
              </p>
              <p className="mt-1 text-sm text-eds-muted-fg">
                {workspaceError}
              </p>

              {selectedApplication && (
                <button
                  type="button"
                  className="mt-3 text-sm font-semibold text-eds-emerald"
                  onClick={() => void openApplication(selectedApplication)}
                >
                  Retry
                </button>
              )}
            </div>
          ) : effectiveApplication ? (
            <div className="space-y-6">
              <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Reference
                  </div>
                  <div className="mt-1 break-all font-mono text-sm text-eds-text">
                    {String(
                      effectiveApplication.reference ??
                        effectiveApplication.id
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Application type
                  </div>
                  <div className="mt-1 text-sm text-eds-text">
                    {formatApplicationStatus(effectiveApplication.type)}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Status
                  </div>
                  <div className="mt-1 text-sm text-eds-text">
                    <AppStatusBadge status={effectiveApplication.status} />
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Current stage
                  </div>
                  <div className="mt-1 text-sm text-eds-text">
                    {formatApplicationStatus(
                      effectiveApplication.currentStage
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Submitted
                  </div>
                  <div className="mt-1 text-sm text-eds-text">
                    {formatApplicationDate(
                      effectiveApplication.submittedAt
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Created
                  </div>
                  <div className="mt-1 text-sm text-eds-text">
                    {formatApplicationDate(
                      effectiveApplication.createdAt
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Last updated
                  </div>
                  <div className="mt-1 text-sm text-eds-text">
                    {formatApplicationDate(
                      effectiveApplication.updatedAt
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                    Record ID
                  </div>
                  <div className="mt-1 break-all font-mono text-sm text-eds-text">
                    {effectiveApplication.id}
                  </div>
                </div>

                {effectiveApplication.title && (
                  <div className="sm:col-span-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                      Title
                    </div>
                    <div className="mt-1 text-sm text-eds-text">
                      {effectiveApplication.title}
                    </div>
                  </div>
                )}

                {effectiveApplication.summary && (
                  <div className="sm:col-span-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-eds-muted-fg">
                      Summary
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-sm text-eds-text">
                      {effectiveApplication.summary}
                    </div>
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold text-eds-text">
                  Workflow timeline
                </h3>

                <div className="mt-3 space-y-2">
                  {applicationTimeline.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-eds-border p-4 text-sm text-eds-muted-fg">
                      No workflow activity has been recorded yet.
                    </div>
                  ) : (
                    applicationTimeline.map((event) => (
                      <div
                        key={event.id}
                        className="rounded-xl border border-eds-border bg-eds-surface-2 p-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-eds-text">
                              {formatApplicationStatus(event.event_type)}
                            </div>

                            <div className="mt-1 text-xs text-eds-muted-fg">
                              {event.from_status || event.to_status
                                ? `${formatApplicationStatus(
                                    event.from_status || undefined
                                  )} → ${formatApplicationStatus(
                                    event.to_status || undefined
                                  )}`
                                : formatApplicationStatus(
                                    event.to_stage ||
                                      event.from_stage ||
                                      undefined
                                  )}
                            </div>
                          </div>

                          <div className="text-xs text-eds-muted-fg">
                            {formatApplicationDate(event.created_at)}
                          </div>
                        </div>

                        {event.notes && (
                          <p className="mt-2 text-sm text-eds-muted-fg">
                            {event.notes}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-eds-text">
                  Documents
                </h3>

                <div className="mt-3 space-y-4">
                  <div className="rounded-xl border border-eds-border bg-eds-surface-2 p-4">
                    <div className="text-sm font-semibold text-eds-text">
                      Upload supporting document
                    </div>

                    <div className="mt-3 space-y-3">
                      <div>
                        <label
                          htmlFor="application-document-type"
                          className="text-sm font-medium text-eds-fg"
                        >
                          Document type
                        </label>
                        <input
                          id="application-document-type"
                          type="text"
                          value={applicationUploadType}
                          onChange={(event) =>
                            setApplicationUploadType(event.target.value)
                          }
                          disabled={applicationUploadBusy}
                          className="mt-1 w-full rounded-md border border-eds-border bg-eds-surface px-3 py-2 text-sm text-eds-text"
                          placeholder="supporting_document"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="application-document-title"
                          className="text-sm font-medium text-eds-fg"
                        >
                          Title
                        </label>
                        <input
                          id="application-document-title"
                          type="text"
                          value={applicationUploadTitle}
                          onChange={(event) =>
                            setApplicationUploadTitle(event.target.value)
                          }
                          disabled={applicationUploadBusy}
                          className="mt-1 w-full rounded-md border border-eds-border bg-eds-surface px-3 py-2 text-sm text-eds-text"
                          placeholder="Optional document title"
                        />
                      </div>

                      <UploadField
                        id="application-document-file"
                        label="File"
                        description="Select the document to associate with this application."
                        maxSizeMb={25}
                        multiple={false}
                        required
                        value={applicationUploadFiles}
                        onChange={setApplicationUploadFiles}
                      />

                      {applicationUploadError ? (
                        <div
                          role="alert"
                          className="text-sm text-eds-danger"
                        >
                          {applicationUploadError}
                        </div>
                      ) : null}

                      {applicationUploadSuccess ? (
                        <div
                          role="status"
                          className="text-sm text-eds-success"
                        >
                          {applicationUploadSuccess}
                        </div>
                      ) : null}

                      <button
                        type="button"
                        onClick={handleApplicationDocumentUpload}
                        disabled={
                          applicationUploadBusy ||
                          applicationUploadFiles.length === 0 ||
                          !applicationUploadType.trim()
                        }
                        className="rounded-md bg-eds-success px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {applicationUploadBusy
                          ? 'Uploading…'
                          : 'Upload document'}
                      </button>
                    </div>
                  </div>

                  {applicationDocuments.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-eds-border p-4 text-sm text-eds-muted-fg">
                      No documents are currently associated with this application.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {applicationDocuments.map((document) => (
                        <div
                          key={document.id}
                          className="rounded-xl border border-eds-border bg-eds-surface-2 p-3"
                        >
                          <div className="text-sm font-semibold text-eds-text">
                            {document.file_name || 'Application document'}
                          </div>
                          <div className="mt-1 text-xs text-eds-muted-fg">
                            {formatApplicationStatus(
                              document.document_type
                            )}
                            {' · '}
                            {formatApplicationStatus(document.status)}
                            {' · '}
                            Version {document.version ?? 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-eds-text">
                  Review activity
                </h3>

                <div className="mt-3">
                  {applicationReviews.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-eds-border p-4 text-sm text-eds-muted-fg">
                      No review activity has been recorded yet.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {applicationReviews.map((review) => (
                        <div
                          key={review.id}
                          className="rounded-xl border border-eds-border bg-eds-surface-2 p-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-eds-text">
                                {formatApplicationStatus(review.decision)}
                              </div>
                              <div className="mt-1 text-xs text-eds-muted-fg">
                                {formatApplicationStatus(
                                  review.review_stage
                                )}
                              </div>
                            </div>

                            <div className="text-xs text-eds-muted-fg">
                              {formatApplicationDate(review.created_at)}
                            </div>
                          </div>

                          {review.comments && (
                            <p className="mt-2 text-sm text-eds-muted-fg">
                              {review.comments}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

const RestrictedDataShell: React.FC<{ ndaSigned: boolean; mouSigned: boolean }> = ({ ndaSigned, mouSigned }) => {
  const [state, setState] = useState<SubmitState>(null);
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true); setState(null);
    const fd = new FormData(e.currentTarget);
    const res = await investorService.requestRestrictedData({
      datasetId: String(fd.get('datasetId') || ''),
      justification: String(fd.get('justification') || ''),
      ndaAcknowledged: fd.get('nda') === 'on',
    });
    setBusy(false);
    setState(res?.error ? { ok: false, error: String(res.error) } : { ok: true });
  };
  return (
    <div className="space-y-4">
      <NdaRequiredNotice signed={ndaSigned} />
      <MouRequiredNotice signed={mouSigned} />
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Dataset ID"><input name="datasetId" className={inputCls} required /></Field>
        <div className="md:col-span-2"><Field label="Justification"><textarea name="justification" rows={3} className={inputCls} required /></Field></div>
        <div className="md:col-span-2 flex items-center gap-2">
          <input id="nda" name="nda" type="checkbox" required />
          <label htmlFor="nda" className="text-sm text-eds-text">I acknowledge the NDA terms.</label>
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button className={submitCls} type="submit" disabled={busy}>{busy ? 'Submitting…' : 'Request access'}</button>
          <SubmitStatus state={state} successLabel="Restricted-data request submitted." />
        </div>
      </form>
    </div>
  );
};

const AgreementsShell: React.FC = () => {
  const [state, setState] = useState<Record<string, SubmitState>>({});
  const submit = async (type: 'NDA' | 'MOU' | 'LOI', e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setState((s) => ({ ...s, [type]: null }));
    const fd = new FormData(e.currentTarget);
    const res = await investorService.requestAgreement({
      type,
      counterparty: String(fd.get('counterparty') || ''),
      scope: String(fd.get('scope') || ''),
    });
    setState((s) => ({
      ...s,
      [type]: res?.error ? { ok: false, error: String(res.error) } : { ok: true },
    }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {(['NDA', 'MOU', 'LOI'] as const).map((t) => (
        <form key={t} onSubmit={(e) => submit(t, e)} className="border border-eds-border rounded-eds-md p-4 space-y-3">
          <h4 className="font-semibold text-eds-text">{t} request</h4>
          <Field label="Counterparty"><input name="counterparty" className={inputCls} required /></Field>
          <Field label="Scope"><textarea name="scope" rows={2} className={inputCls} required /></Field>
          <button className={submitCls} type="submit">Request {t}</button>
          <div className="text-xs">
            <SubmitStatus state={state[t] ?? null} successLabel={`${t} request submitted.`} />
          </div>
        </form>
      ))}
    </div>
  );
};

/* Legacy PaymentsShell / SupportShell / NotificationsShell removed — replaced by
   PaymentsCenter, SupportEnquiryForm + NotificationPreferencesPanel, and
   NotificationHistoryPanel respectively. */

export default InvestorDashboard;
